/* eslint-disable @typescript-eslint/no-unsafe-assignment --
   The reactified ymaps3 components and the imperative map instance are
   dynamically typed at the CDN boundary (same containment as `map-canvas.tsx`). */
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@noor/lib';
import type { YmapsBindings } from '@noor/maps';
import { MAP_CUSTOMIZATION } from '../lib/map-customization';
import { resolveToneColor } from '../lib/offer-colors';
import { outcomeLabel, shortCourierId } from '../lib/offer-labels';
import { useBackgroundClick } from '../lib/use-background-click';
import type { OfferMarker } from '../lib/offer-markers';
import { OfferBadgeFace, OriginPinFace } from './offer-faces';
import { OffersMapControls } from './offers-map-controls';

type Bounds = [[number, number], [number, number]];
interface MapInstance {
  update: (opts: unknown) => void;
  zoom?: number;
}

export interface OffersCanvasProps {
  origin: { coords: [number, number]; label?: string | undefined };
  markers: OfferMarker[];
  bounds: Bounds;
  /** Framing is applied once per key; later data refreshes never move the camera. */
  fitKey: string;
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  className?: string | undefined;
}

const FIT_PADDING = 56;

/**
 * Offers visualization on Yandex Maps v3: the pickup pin, one badge per courier
 * and a line from each courier to the pickup. Badges are real buttons, so the
 * map is operable by keyboard and screen reader.
 */
export function OffersCanvasYandex({
  bindings,
  origin,
  markers,
  bounds,
  fitKey,
  selectedKey,
  onSelect,
  className,
}: OffersCanvasProps & { bindings: YmapsBindings }) {
  const { t } = useTranslation('orders');
  const { ymaps3, reactify } = bindings;
  const mapRef = useRef<MapInstance | null>(null);
  const backgroundClick = useBackgroundClick(() => onSelect(null));

  const components = useMemo(() => reactify.module(ymaps3), [reactify, ymaps3]);
  const {
    YMap,
    YMapDefaultSchemeLayer,
    YMapDefaultFeaturesLayer,
    YMapMarker,
    YMapFeature,
  } = components;

  // `useDefault`: apply the framing on mount (and per `fitKey`), then leave the
  // camera to the user — a socket refresh must not yank the map away.
  const location = reactify.useDefault({ bounds }, [fitKey]);

  const lines = useMemo(
    () =>
      markers.map((marker) => ({
        key: marker.key,
        color: resolveToneColor(marker.outcome.tone),
      })),
    [markers],
  );

  const handleRef = useCallback((instance: MapInstance | null) => {
    mapRef.current = instance;
  }, []);

  const fit = useCallback(() => {
    mapRef.current?.update({ location: { bounds, duration: 400 } });
  }, [bounds]);

  const zoomBy = useCallback((delta: number) => {
    const map = mapRef.current;
    if (!map) return;
    const current = typeof map.zoom === 'number' ? map.zoom : 13;
    map.update({ location: { zoom: current + delta, duration: 300 } });
  }, []);

  if (!YMap) return null;

  return (
    <div className={cn('relative overflow-hidden', className)} {...backgroundClick}>
      <YMap
        ref={handleRef}
        location={location}
        mode="vector"
        margin={[FIT_PADDING, FIT_PADDING, FIT_PADDING, FIT_PADDING]}
        style={{ width: '100%', height: '100%' }}
      >
        <YMapDefaultSchemeLayer customization={MAP_CUSTOMIZATION} />
        <YMapDefaultFeaturesLayer />
        {YMapFeature &&
          markers.map((marker, i) => {
            const emphasised = marker.key === selectedKey || marker.active;
            return (
              <YMapFeature
                key={`line-${marker.key}`}
                geometry={{
                  type: 'LineString',
                  coordinates: [marker.coords, origin.coords],
                }}
                style={{
                  stroke: [
                    { width: emphasised ? 4 : 2, color: lines[i]?.color ?? 'gray' },
                  ],
                  opacity: emphasised ? 1 : marker.opacity,
                }}
              />
            );
          })}

        <YMapMarker coordinates={origin.coords} zIndex={1500}>
          <span className="block -translate-x-1/2 -translate-y-1/2">
            <OriginPinFace label={origin.label} />
          </span>
        </YMapMarker>

        {markers.map((marker) => {
          const selected = marker.key === selectedKey;
          return (
            <YMapMarker
              key={marker.key}
              coordinates={marker.coords}
              zIndex={selected ? 3000 : marker.active ? 2000 : 1000}
            >
              <button
                type="button"
                aria-pressed={selected}
                aria-label={t('offers.markerLabel', {
                  id: shortCourierId(marker.offer.courierId) ?? '—',
                  km: marker.badgeKm.toFixed(1),
                  outcome: outcomeLabel(t, marker.outcome),
                })}
                onClick={() => onSelect(selected ? null : marker.key)}
                className="block -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60"
              >
                <OfferBadgeFace
                  tone={marker.outcome.tone}
                  km={marker.badgeKm}
                  opacity={selected || marker.active ? 1 : marker.opacity}
                  selected={selected}
                  active={marker.active}
                />
              </button>
            </YMapMarker>
          );
        })}
      </YMap>

      <OffersMapControls
        className="bottom-12"
        onFit={fit}
        onZoomIn={() => zoomBy(1)}
        onZoomOut={() => zoomBy(-1)}
      />
    </div>
  );
}
